"use client"

import * as React from "react"
import { usePostHog } from "posthog-js/react"
import { BrandsHero } from "./_components/brands-hero"
import { FeaturedLogoBar } from "./_components/featured-logo-bar"
import { BrandsToolbar } from "./_components/brands-toolbar"
import { AlphabetRail } from "./_components/alphabet-rail"
import { BrandSection } from "./_components/brand-section"
import { BrandCardList } from "./_components/brand-card-list"
import { EmptyState } from "./_components/empty-state"
import {
  letterKey,
  type IndexBrand,
  type Sort,
  type TierFilter,
} from "./_components/types"

interface BrandsIndexClientProps {
  brands: IndexBrand[]
  /** Brands marked `is_partner = true`, ordered by product count. */
  partners: IndexBrand[]
}

/**
 * Brands index client orchestrator. Owns search / tier / sort state, derives
 * filtered + sorted + grouped collections via `useMemo`, and renders the
 * hero, featured-logo bar, sticky toolbar, A–Z rail, and letter sections.
 *
 * View mode is always "list" — the previous Logos/grid toggle was removed
 * after the list view was confirmed as the canonical browsing surface.
 */
const PageContainer = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`mx-auto w-full max-w-[1280px] px-6 sm:px-8 lg:px-12 ${className}`}>
    {children}
  </div>
)

export default function BrandsIndexClient({ brands, partners }: BrandsIndexClientProps) {
  const posthog = usePostHog()

  // ─── State ───
  const [query, setQuery] = React.useState("")
  const [tier, setTier] = React.useState<TierFilter>("all")
  const [sort, setSort] = React.useState<Sort>("alphabetical")
  const [currentLetter, setCurrentLetter] = React.useState<string | null>(null)
  const sectionRefs = React.useRef<Record<string, HTMLElement | null>>({})

  React.useEffect(() => {
    if (posthog) {
      posthog.capture("brand_index_viewed", {
        brand_count: brands.length,
        partner_count: partners.length,
      })
    }
  }, [posthog, brands.length, partners.length])

  // ─── Derived data ───
  //
  // FRIDGE-1502 (N1): the grid reads deferred copies of the search, tier and sort,
  // so the tab or select a shopper touched updates in the next frame and the
  // grid re-renders behind it, interruptibly. Measured 10/14/2026 on a
  // 4x-slowed phone: the tier tabs took 216-248 ms and the Sort select 240-288 ms,
  // because the whole grid (about 145 cards) re-rendered before the next paint.
  const listQuery = React.useDeferredValue(query)
  const listTier = React.useDeferredValue(tier)
  const listSort = React.useDeferredValue(sort)

  /** Filter by query (name + types haystack) and tier. */
  const filtered = React.useMemo(() => {
    const q = listQuery.trim().toLowerCase()
    return brands.filter(b => {
      if (listTier !== "all" && b.tier !== listTier) return false
      if (q) {
        const hay = `${b.name} ${b.types.join(" ")}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [brands, listQuery, listTier])

  /** Apply sort. Non-alphabetical sorts flatten letter groupings. */
  const sorted = React.useMemo(() => {
    const list = [...filtered]
    switch (listSort) {
      case "alphabetical":
        list.sort((a, b) => a.name.localeCompare(b.name))
        break
      case "popularity":
        list.sort((a, b) => b.productCount - a.productCount)
        break
      case "price-low":
        list.sort((a, b) => (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity))
        break
      case "price-high":
        list.sort((a, b) => (b.maxPrice ?? -Infinity) - (a.maxPrice ?? -Infinity))
        break
    }
    return list
  }, [filtered, listSort])

  /** Group by first letter — only when sort is alphabetical. */
  const grouped = React.useMemo<Record<string, IndexBrand[]> | null>(() => {
    if (listSort !== "alphabetical") return null
    const out: Record<string, IndexBrand[]> = {}
    for (const b of sorted) {
      const L = letterKey(b.name)
      ;(out[L] ||= []).push(b)
    }
    return out
  }, [sorted, listSort])

  /** Letters that currently have brands — drives rail enabled state. */
  const activeLetters = React.useMemo(() => {
    const s = new Set<string>()
    for (const b of filtered) s.add(letterKey(b.name))
    return s
  }, [filtered])

  /** Sorted section keys: A→Z then `#` last. */
  const sectionKeys = React.useMemo(() => {
    if (!grouped) return []
    return Object.keys(grouped).sort((a, b) => {
      if (a === "#") return 1
      if (b === "#") return -1
      return a.localeCompare(b)
    })
  }, [grouped])

  /** IntersectionObserver-driven rail highlight. Top rootMargin cuts the
   *  observer's viewport to a band just under the sticky toolbar (~150px),
   *  so "active" follows whichever section's top edge has crossed below
   *  the toolbar. */
  // Clearing the active letter when grouping switches off is state derived from
  // `grouped`, so it is adjusted during render rather than from the effect
  // below (react-hooks/set-state-in-effect). Only the transition needs
  // handling — on a first render with grouping off, currentLetter is already
  // null. The observer setup stays in the effect, where it belongs.
  const [prevGrouped, setPrevGrouped] = React.useState(grouped)
  if (prevGrouped !== grouped) {
    setPrevGrouped(grouped)
    if (!grouped) setCurrentLetter(null)
  }

  React.useEffect(() => {
    if (!grouped) {
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          const L = (visible[0].target as HTMLElement).dataset.letter
          if (L) setCurrentLetter(L)
        }
      },
      { rootMargin: "-150px 0px -60% 0px", threshold: 0 },
    )
    for (const L of Object.keys(grouped)) {
      const el = sectionRefs.current[L]
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [grouped, sectionKeys])

  /** Smooth-scroll to a letter section, offsetting by sticky-header height. */
  const jumpToLetter = React.useCallback((L: string) => {
    const el = sectionRefs.current[L]
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY - 150
    window.scrollTo({ top, behavior: "smooth" })
  }, [])

  const reset = React.useCallback(() => {
    setQuery("")
    setTier("all")
  }, [])

  // ─── Render ───
  //
  // Layout strategy: ONE outer container (`PageContainer`) owns horizontal
  // padding for the hero + featured bar + sections. The sticky toolbar is
  // the only full-bleed element so its bottom border can extend edge-to-
  // edge — it nests its own matching container internally so its content
  // aligns with everything above and below.

  return (
    <>
      <PageContainer>
        <BrandsHero
          miniStackBrands={partners.filter(b => b.logo_path).slice(0, 6)}
          totalCount={brands.length}
        />
      </PageContainer>

      <PageContainer>
        <FeaturedLogoBar partners={partners.slice(0, 6)} />
      </PageContainer>

      <div id="brand-directory" className="scroll-mt-[120px]">
        <BrandsToolbar
          query={query}
          setQuery={setQuery}
          tier={tier}
          setTier={setTier}
          sort={sort}
          setSort={setSort}
          totalCount={brands.length}
          shownCount={filtered.length}
        />

        <PageContainer className="pt-8 pb-20 lg:pb-28">
          {grouped && (
            <div className="text-center">
              <AlphabetRail
                activeLetters={activeLetters}
                currentLetter={currentLetter}
                onJump={jumpToLetter}
              />
            </div>
          )}

          {filtered.length === 0 ? (
            <EmptyState query={query} onReset={reset} />
          ) : grouped ? (
            sectionKeys.map(L => (
              <BrandSection
                key={L}
                letter={L}
                brands={grouped[L]}
                sectionRef={el => {
                  sectionRefs.current[L] = el
                }}
              />
            ))
          ) : (
            // Non-alphabetical sort flattens groups → just render the cards
            // in order without the letter section headers.
            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {sorted.map(b => (
                <BrandCardList key={b.slug} brand={b} />
              ))}
            </div>
          )}
        </PageContainer>
      </div>
    </>
  )
}
